import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Users2, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRepFilter } from "@/context/RepFilterContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AgendaItem, { KIND } from "@/components/AgendaItem";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const ymOf = (d) => iso(d).slice(0, 7);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d; };

function greeting(h) {
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function useMinuteClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    let interval;
    const timeout = setTimeout(() => { tick(); interval = setInterval(tick, 60000); }, 60000 - (Date.now() % 60000));
    return () => { clearTimeout(timeout); if (interval) clearInterval(interval); };
  }, []);
  return now;
}

function Agenda({ date, items, onOpen, user, onChanged }) {
  const label = new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const isToday = date === iso(new Date());
  const isAdmin = user?.role === "admin";
  const emailMe = async () => {
    try { const { data } = await api.post("/agenda/send-mine", null, { params: { date } }); toast.success(`Agenda sent to ${data.sent_to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  return (
    <div className="pt-2 border-t border-slate-100" data-testid="sidebar-agenda">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500" data-testid="sidebar-agenda-date">{isToday ? "Today" : label} · {items.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={emailMe} className="text-slate-500 hover:text-slate-900" title="Email me this day's agenda" data-testid="sidebar-agenda-email"><Mail className="w-3 h-3" /></button>
          <button onClick={onOpen} className="text-[10px] font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-0.5" data-testid="sidebar-agenda-open">Planning <ArrowUpRight className="w-3 h-3" /></button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-slate-400 py-1" data-testid="sidebar-agenda-empty">Nothing scheduled.</div>
      ) : (
        <ul className="space-y-1 max-h-44 overflow-y-auto pr-0.5">
          {items.map(e => <AgendaItem key={`${e.kind}-${e.id}`} e={e} canAct={isAdmin || !e.owner_id || e.owner_id === user?.user_id} onChanged={onChanged} />)}
        </ul>
      )}
    </div>
  );
}

function MiniCalendar({ repId, user }) {
  const nav = useNavigate();
  const todayIso = iso(new Date());
  const [cursor, setCursor] = useState(firstOfMonth);
  const [selected, setSelected] = useState(todayIso);
  const [events, setEvents] = useState([]);
  const [tick, setTick] = useState(0);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/dashboard/calendar", { params: { month: ymOf(cursor), rep_id: isAdmin && repId !== "all" ? repId : undefined, mine: isAdmin ? undefined : true } })
      .then(({ data }) => setEvents(data.events || [])).catch(() => setEvents([]));
  }, [cursor, repId, isAdmin, tick]);

  const byDay = useMemo(() => {
    const m = {};
    events.forEach(e => { (m[e.date] ||= []).push(e); });
    return m;
  }, [events]);

  const cells = useMemo(() => {
    const out = [];
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    for (let i = 0; i < cursor.getDay(); i++) out.push(null);
    for (let d = 1; d <= days; d++) out.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (out.length % 7) out.push(null);
    return out;
  }, [cursor]);

  const shift = (n) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const openPlanning = (k) => nav(`/planning?view=day&date=${k}`);
  const pick = (k) => { if (k === selected) openPlanning(k); else setSelected(k); };
  const agendaItems = selected.slice(0, 7) === ymOf(cursor) ? (byDay[selected] || []) : [];

  return (
    <div data-testid="sidebar-calendar">
      <div className="flex items-center justify-between mb-1">
        <button className="p-1 rounded hover:bg-slate-100" onClick={() => shift(-1)} data-testid="sidebar-cal-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
        <button className="text-xs font-semibold text-slate-800 hover:underline" onClick={() => { setCursor(firstOfMonth()); setSelected(todayIso); }} data-testid="sidebar-cal-month">
          {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </button>
        <button className="p-1 rounded hover:bg-slate-100" onClick={() => shift(1)} data-testid="sidebar-cal-next"><ChevronRight className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-7 text-center text-[10px] text-slate-400 font-mono-plex mb-0.5">
        {DAYS.map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 mb-2">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const k = iso(d);
          const kinds = byDay[k] ? [...new Set(byDay[k].map(e => e.kind))] : null;
          const isToday = k === todayIso;
          const isSel = k === selected;
          return (
            <button
              key={i}
              onClick={() => pick(k)}
              onDoubleClick={() => openPlanning(k)}
              data-testid={`sidebar-cal-day-${k}`}
              title={isSel ? "Click again to open in Planning" : (kinds ? `${byDay[k].length} activities` : "Select day")}
              className={`relative mx-auto w-7 h-7 rounded-full text-[11px] flex flex-col items-center justify-center transition-colors duration-150 ${
                isToday ? "bg-slate-900 text-white font-semibold" : isSel ? "bg-slate-200 text-slate-900 font-semibold" : "text-slate-700 hover:bg-slate-100"
              } ${isSel && isToday ? "ring-2 ring-slate-400 ring-offset-1" : ""}`}
            >
              <span className="leading-none">{d.getDate()}</span>
              {kinds && (
                <span className="absolute bottom-0.5 flex gap-px">
                  {kinds.slice(0, 3).map(kind => <span key={kind} className={`w-1 h-1 rounded-full ${isToday ? "bg-white/80" : KIND[kind]?.dot || "bg-slate-400"}`} />)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <Agenda date={selected} items={agendaItems} onOpen={() => openPlanning(selected)} user={user} onChanged={() => setTick(t => t + 1)} />
    </div>
  );
}

export default function SidebarGreeting({ className = "" }) {
  const { user } = useAuth();
  const { repId, setRepId, reps, isAdmin } = useRepFilter();
  const now = useMinuteClock();
  const firstName = (user?.name || "").split(" ")[0] || "there";
  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  return (
    <div className={`px-4 py-4 space-y-3 ${className}`} data-testid="sidebar-greeting">
      <div>
        <div className="text-sm font-semibold text-slate-900 truncate" data-testid="sidebar-welcome">{greeting(now.getHours())}, {firstName}</div>
        <div className="text-[11px] text-slate-500 font-mono-plex mt-0.5" data-testid="sidebar-datetime">{dateStr} · {timeStr}</div>
      </div>
      {isAdmin && (
        <Select value={repId} onValueChange={setRepId}>
          <SelectTrigger className="h-8 text-xs" data-testid="sidebar-rep-filter">
            <Users2 className="w-3.5 h-3.5 mr-1.5 text-slate-500 shrink-0" />
            <SelectValue placeholder="All reps" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reps</SelectItem>
            {reps.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <MiniCalendar repId={repId} user={user} />
    </div>
  );
}
